import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import {
  httpQueryClient,
  useMutation,
} from '@/domains/core/httpClient/queryClient';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { useAuth } from '@/domains/customerManagement/auth/hooks/useAuth';
import {
  addToWishlistFailText,
  deleteFromWishlistFailText,
  returnButtonCta,
} from '@/domains/proB2BServices/AddToWishlist/translations';
import { useMainListCard } from '@/domains/proB2BServices/Wishlists/modules/MainList/MainListCard/useMainListCard';
import { useInteractionModal } from '@/domains/proB2BServices/Wishlists/modules/WishlistNavigation/InteractionModal/useInteractionModal';
import { closeModalAction } from '@/domains/proB2BServices/Wishlists/store/Wishlists.actions';
import { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import { useQuantity } from '@/domains/productDiscovery/ProductPage/hooks/useQuantity';

import { useMainListFetch } from '../../Wishlists/hooks/useMainListFetch';
import { useWishlistServices } from '../../Wishlists/hooks/useWishlistServices';
import {
  WishlistDetailsContent,
  WishlistUnit,
} from '../../Wishlists/interfaces';
import { IWishlishServiceCalls } from '../../Wishlists/wishlistsService';
import { ABTests } from '../abTests';
import { ID_WISHLIST_ALL_PRODUCTS, STATUS, STEPS } from '../constants';
import { GTM_EVENTS_DATA } from '../gtmEvents';
import { useHeaderWishlistMiniPopup } from './useHeaderWishlistMiniPopup';
import { useNewList } from './useNewList';
import { useOperationFeedback } from './useOperationFeedback';
import { useWishlistsListCard } from './useWishlistsListCard';

type UseAddToWishlistProps = {
  productId: number;
  onModalClose?: (step: STEPS, status: STATUS) => void;
  hideNotificationActionText?: boolean;
  addProductToAllWishListCallback?: () => void;
};

export const useAddToWishlist = ({
  productId,
  onModalClose,
  hideNotificationActionText,
  addProductToAllWishListCallback,
}: UseAddToWishlistProps) => {
  const { isAuthenticated, isGuest } = useAuth();
  const [step, setStep] = useState(STEPS.LISTS);
  const setInitialStep = () => setStep(STEPS.LISTS);
  const [isFetchEnabled, setFetchEnabled] = useState(false);
  const imageUrl = useSelector(productPageThumbnailSelector);
  const [isWishlistRevampEnabled] = useBooleanFlags([
    ABTests.WISHLIST_REVAMP_ENABLED,
  ]);
  const [isProductInWishlistNow, setProductInWishlistNow] = useState(false);
  const dispatch = useDispatch();

  const [list, setList] = useState<{ name: string; id: number | string }>({
    name: '',
    id: '',
  });
  const [feedback, setFeedback] = useState<STATUS>(STATUS.SUCCESS);
  const [isFetchInProgress, setIsFetchInProgress] = useState(false);

  const quantity = useQuantity();
  const { calls } = useWishlistServices();

  useEffect(() => {
    if (isWishlistRevampEnabled && !isGuest && isAuthenticated) {
      isProductAlreadyInWishlist(calls, productId).then((flag) => {
        setProductInWishlistNow(flag);
      });
    }
  }, [productId, isWishlistRevampEnabled, calls, isAuthenticated, isGuest]);

  const {
    fetchData: { data, isFetching: isMainListFetching, isFetched, refetch },
  } = useMainListFetch(isFetchEnabled);

  if (isWishlistRevampEnabled) {
    const hasListOtherThanAllProducts = data && data.length > 1;
    if (!hasListOtherThanAllProducts && isFetched) {
      if (step !== STEPS.NEW_LIST) setStep(STEPS.NEW_LIST);
    }
  } else {
    if (!data?.length && isFetched) {
      if (step === STEPS.LISTS) setStep(STEPS.NEW_LIST);
    }
  }

  const { mutate: onAddProduct, isPending: isAddingProduct } = useMutation({
    mutationFn: (wishlistId: number | string) =>
      calls.addProduct(wishlistId, productId, quantity),
  });

  const handleError = useCallback(() => {
    setFeedback(STATUS.DANGER);
    setStep(STEPS.FEEDBACK);
  }, []);

  const onAddToList = (id: number | string, name: string) => {
    setList({ name, id });

    if (!isAddingProduct) {
      onAddProduct(id, {
        onSuccess: () => {
          Gtm.push({
            ...GTM_EVENTS_DATA.ADD_TO_WISHLIST,
            event_trigger: 'Added to wishlist',
          });

          if (isWishlistRevampEnabled) {
            dispatch(closeModalAction());
            actionShowMiniPopupSuccess(name, imageUrl || '');
            if (onModalClose) {
              onModalClose(STEPS.FEEDBACK, STATUS.SUCCESS);
            }
          } else {
            setFeedback(STATUS.SUCCESS);
            setStep(STEPS.FEEDBACK);
          }
        },
        onError: handleError,
      });
    }
  };

  const newListProps = useNewList(onAddToList, handleError);
  const wislistsListProps = useWishlistsListCard(
    data,
    isMainListFetching,
    onAddToList,
  );
  const feedbackProps = useOperationFeedback(list.name, feedback);
  const isNewListDisabled = isMainListFetching || (data && data?.length >= 10);

  const { goToWishlist } = useMainListCard(list.id);

  const getModalData = () => {
    switch (step) {
      case STEPS.NEW_LIST:
        return {
          ...newListProps,
          onSecondaryButtonClick: () => {
            if (isWishlistRevampEnabled) {
              newListProps.onActionClose();
              if (data && data?.length > 1) {
                setInitialStep();
              } else {
                setInitialStep();
                dispatch(closeModalAction());
                if (onModalClose) {
                  onModalClose(step, feedback);
                }
              }
            } else {
              setInitialStep();
            }
          },
          keepOpenOnSuccess: true,
        };
      case STEPS.FEEDBACK:
        return {
          ...feedbackProps,
          onActionSuccess: setInitialStep,
          showSecondaryButton: true,
          secondaryButtonLabel: returnButtonCta,
          onSecondaryButtonClick: goToWishlist(list.id),
        };
      default:
        return {
          ...wislistsListProps,
          onActionSuccess: () => {
            setStep(STEPS.NEW_LIST);
          },
          keepOpenOnSuccess: true,
          disableMainCta: isNewListDisabled,
        };
    }
  };

  const { modal, onModalOpen: openBaseModal } = useInteractionModal({
    modalId: 'modal-add-to-wishlist',
    performingAction: isAddingProduct,
    onActionClose: () => {
      if (onModalClose) {
        onModalClose(step, feedback);
      }
    },
    maxWidth: 'lg',
    ...getModalData(),
  });

  const onModalOpen = useCallback(() => {
    setFetchEnabled(true);
    refetch();
    setInitialStep();
    openBaseModal();
  }, [openBaseModal, refetch]);

  const onHeaderMiniPopupActionClick = useCallback(() => {
    onModalOpen();
  }, [onModalOpen]);

  const {
    actionShowMiniPopupSuccess,
    actionShowMiniPopupFailure,
    actionShowMiniPopupWithAction,
    actionShowMiniPopupDeleted,
  } = useHeaderWishlistMiniPopup(
    onHeaderMiniPopupActionClick,
    hideNotificationActionText,
  );

  const addProductToAllWishList = useCallback(() => {
    setProductInWishlistNow(true);
    setIsFetchInProgress(true);
    calls
      .addProduct(ID_WISHLIST_ALL_PRODUCTS, productId, quantity)
      .then(() => {
        if (addProductToAllWishListCallback) {
          addProductToAllWishListCallback();
        }
        actionShowMiniPopupWithAction(imageUrl || '');
        if (onModalClose) {
          onModalClose(STEPS.FEEDBACK, STATUS.SUCCESS);
        }
        setIsFetchInProgress(false);
      })
      .catch(() => {
        setProductInWishlistNow(false);
        setIsFetchInProgress(false);
        if (isWishlistRevampEnabled) {
          actionShowMiniPopupFailure(addToWishlistFailText);
        } else {
          handleError();
        }
      });
  }, [
    calls,
    productId,
    quantity,
    addProductToAllWishListCallback,
    actionShowMiniPopupWithAction,
    imageUrl,
    onModalClose,
    isWishlistRevampEnabled,
    actionShowMiniPopupFailure,
    handleError,
  ]);

  const deleteProductFromWishlist = useCallback(() => {
    setProductInWishlistNow(false);
    setIsFetchInProgress(true);
    calls
      .deleteProduct(ID_WISHLIST_ALL_PRODUCTS, productId)
      .then(() => {
        setIsFetchInProgress(false);
        actionShowMiniPopupDeleted(imageUrl || '');
      })
      .catch(() => {
        setProductInWishlistNow(true);
        setIsFetchInProgress(false);
        if (isWishlistRevampEnabled) {
          actionShowMiniPopupFailure(deleteFromWishlistFailText);
        } else {
          handleError();
        }
      });
  }, [
    calls,
    productId,
    imageUrl,
    actionShowMiniPopupDeleted,
    actionShowMiniPopupFailure,
    handleError,
    isWishlistRevampEnabled,
  ]);
  return {
    modal,
    isProductInWishlistNow,
    isFetching: isMainListFetching || isFetchInProgress,
    onModalOpen,
    onModalClose,
    addProductToAllWishList,
    deleteProductFromWishlist,
  };
};

async function fetchProductInfo(
  calls: IWishlishServiceCalls,
  wishlistID: string | number,
): Promise<WishlistDetailsContent | undefined> {
  const response = await httpQueryClient.fetchQuery({
    queryKey: ['', String(wishlistID)],
    meta: undefined,
    queryFn: calls.getOne,
  });
  return response;
}

async function fetchWishlistData(
  calls: IWishlishServiceCalls,
): Promise<WishlistUnit[] | undefined> {
  const response = await httpQueryClient.fetchQuery({
    queryKey: [''],
    queryFn: calls.getAll,
  });
  return response;
}

export async function isProductAlreadyInWishlist(
  calls: IWishlishServiceCalls,
  productId: number,
) {
  const wishlistOnlyData = await fetchWishlistData(calls);
  if (wishlistOnlyData && wishlistOnlyData.length > 0) {
    const wishlists = wishlistOnlyData;
    for (let wishlistIdx = 0; wishlistIdx < wishlists.length; wishlistIdx++) {
      const listResp = await fetchProductInfo(calls, wishlists[wishlistIdx].id);
      if (listResp && listResp.products && listResp.products.length > 0) {
        const isAvailable = listResp.products
          .map((product) => product.id)
          .includes(productId);
        if (isAvailable) {
          return true;
        }
      }
    }
  }
  return false;
}

function productPageThumbnailSelector(
  state: Partial<SpartacuxProductDiscoveryDomainState>,
) {
  const detailData = state.productDiscovery?.productPage?.detail?.data;
  if (detailData) {
    return detailData.media[0].thumbnailUrl;
  }
  return '';
}
